/**
 * Incident Editor Form Hook (PROJ-007)
 * Manages form state, submission, and error handling
 */

import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useForm, type Resolver, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import type { Incident } from '@/types/incidents';
import type { IncidentFormData, IncidentEditorMode } from './types';

// Validation Schema - Complete CRM fields
const incidentSchema = z.object({
  // Identification
  incidentNumber: z.string().optional(),
  externalRef: z.string().optional(),

  // Content
  title: z.string().min(1, 'El título es requerido').max(255, 'El título no puede exceder 255 caracteres'),
  description: z.string().optional(),
  details: z.string().optional(),
  sources: z.string().optional(),

  // Categorization
  industry: z.string().min(1, 'La industria es requerida'),
  category: z.string().optional(),
  subcategory: z.string().optional(),
  type: z.string().min(1, 'El tipo de incidente es requerido'),

  // Priority & Impact
  priority: z.string().min(1, 'La prioridad es requerida'),
  impact: z.string().optional(),
  urgency: z.string().optional(),
  severity: z.string().min(1, 'La severidad es requerida'),
  origin: z.string().optional(),

  // Lifecycle
  state: z.string().default('new'),

  // Dates
  reportedAt: z.string().min(1, 'La fecha de reporte es requerida'),
  detectedAt: z.string().optional(),
  dueAt: z.string().optional(),
  assignedAt: z.string().optional(),

  // Resolution
  resolution: z.string().optional(),
  resolutionType: z.string().optional(),
  resolvedAt: z.string().optional(),
  closedAt: z.string().optional(),

  // People (Who)
  requesterUserId: z.string().optional(),
  ownerUserId: z.string().optional(),
  assignedToUserId: z.string().optional(),
  resolvedByUserId: z.string().optional(),
  closedByUserId: z.string().optional(),

  // Teams & Organization
  owningTeamId: z.string().optional(),
  owningBuId: z.string().optional(),

  // Customer
  customerType: z.string().optional(),
  customerId: z.string().optional(),

  // Relationships
  parentIncidentId: z.string().optional(),
  configurationItemId: z.string().optional(),
  locationId: z.string().optional(),

  // Privacy & Compliance
  isPrivacy: z.boolean().default(true),
  isBreach: z.boolean().default(false),
  needsDpia: z.boolean().default(false),
  involvesMinors: z.boolean().default(false),
  involvesHealth: z.boolean().default(false),

  // SLA
  slaPolicyId: z.string().optional(),
});

interface UseIncidentEditorFormProps {
  mode: IncidentEditorMode;
  incident: Incident | null;
  open: boolean;
  onSubmit: (values: IncidentFormData) => void | Promise<void>;
  onClose: () => void;
}

const toInputDateTime = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  try {
    // Normalize to the browser-friendly YYYY-MM-DDTHH:MM shape used by datetime-local
    const iso = new Date(value).toISOString();
    return iso.slice(0, 16);
  } catch (error) {
    console.warn('[IncidentEditor] Failed to normalize date value, falling back to raw slice:', value, error);
    return value.length >= 16 ? value.slice(0, 16) : value;
  }
};

const DEFAULT_SUBMIT_ERROR = 'Error desconocido al guardar incidente';

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null) {
    const apiError = error as { message?: string; response?: { data?: { message?: string } } };

    if (apiError.response?.data?.message) {
      return apiError.response.data.message;
    }

    if (apiError.message) {
      return apiError.message;
    }
  }

  return DEFAULT_SUBMIT_ERROR;
};

const buildDefaultValues = (incident: Incident | null, mode: IncidentEditorMode): IncidentFormData => {
  if (mode === 'edit' && incident) {
    return {
      // Identification
      incidentNumber: incident.incidentNumber,
      externalRef: incident.externalRef || '',

      // Content
      title: incident.title || incident.summary || '',
      description: incident.description || '',
      details: incident.details ? JSON.stringify(incident.details, null, 2) : '',
      sources: incident.sources?.join(', ') || '',

      // Categorization
      industry: incident.industry || '',
      category: incident.category || '',
      subcategory: incident.subcategory || '',
      type: incident.type || '',

      // Priority & Impact
      priority: incident.priority || 'medium',
      impact: incident.impact || '',
      urgency: incident.urgency || '',
      severity: incident.severity || 'medium',
      origin: incident.origin || '',

      // Lifecycle
      state: incident.statusReason || incident.state || 'new',

      // Dates
      reportedAt: toInputDateTime(incident.reportedAt) || new Date().toISOString().slice(0, 16),
      detectedAt: toInputDateTime(incident.detectedAt),
      dueAt: toInputDateTime(incident.dueAt),
      assignedAt: toInputDateTime(incident.assignedAt),

      // Resolution
      resolution: incident.resolution || '',
      resolutionType: incident.resolutionType || '',
      resolvedAt: toInputDateTime(incident.resolvedAt),
      closedAt: toInputDateTime(incident.closedAt),

      // People (Who)
      requesterUserId: incident.requesterUserId || '',
      ownerUserId: incident.ownerUserId || '',
      assignedToUserId: incident.assignedToUserId || '',
      resolvedByUserId: incident.resolvedByUserId || '',
      closedByUserId: incident.closedByUserId || '',

      // Teams & Organization
      owningTeamId: incident.owningTeamId || '',
      owningBuId: incident.owningBuId || '',

      // Customer
      customerType: incident.customerType || '',
      customerId: incident.customerId || '',

      // Relationships
      parentIncidentId: incident.parentIncidentId || '',
      configurationItemId: incident.configurationItemId || '',
      locationId: incident.locationId || '',

      // Privacy & Compliance
      isPrivacy: incident.isPrivacy ?? true,
      isBreach: incident.isBreach ?? false,
      needsDpia: incident.needsDpia ?? false,
      involvesMinors: incident.involvesMinors ?? false,
      involvesHealth: incident.involvesHealth ?? false,

      // SLA
      slaPolicyId: incident.slaPolicyId || '',
    };
  }

  // Create mode defaults
  return {
    // Identification
    incidentNumber: undefined,
    externalRef: '',

    // Content
    title: '',
    description: '',
    details: '',
    sources: '',

    // Categorization
    industry: '',
    category: '',
    subcategory: '',
    type: '',

    // Priority & Impact
    priority: 'medium',
    impact: '',
    urgency: '',
    severity: 'medium',
    origin: 'web',

    // Lifecycle
    state: 'new',

    // Dates
    reportedAt: new Date().toISOString().slice(0, 16),
    detectedAt: '',
    dueAt: '',
    assignedAt: '',

    // Resolution
    resolution: '',
    resolutionType: '',
    resolvedAt: '',
    closedAt: '',

    // People (Who)
    requesterUserId: '',
    ownerUserId: '',
    assignedToUserId: '',
    resolvedByUserId: '',
    closedByUserId: '',

    // Teams & Organization
    owningTeamId: '',
    owningBuId: '',

    // Customer
    customerType: '',
    customerId: '',

    // Relationships
    parentIncidentId: '',
    configurationItemId: '',
    locationId: '',

    // Privacy & Compliance
    isPrivacy: true,
    isBreach: false,
    needsDpia: false,
    involvesMinors: false,
    involvesHealth: false,

    // SLA
    slaPolicyId: '',
  };
};

interface SyncFormStateArgs {
  open: boolean;
  incident: Incident | null;
  mode: IncidentEditorMode;
  form: UseFormReturn<IncidentFormData>;
  setSubmitError: (message: string | null) => void;
  previousIncidentIdRef: MutableRefObject<string | null>;
  previousOpenRef: MutableRefObject<boolean>;
}

const syncFormStateOnDialogChanges = ({
  open,
  incident,
  mode,
  form,
  setSubmitError,
  previousIncidentIdRef,
  previousOpenRef
}: SyncFormStateArgs): void => {
  const currentIncidentId = incident?.id ?? null;
  const previousIncidentId = previousIncidentIdRef.current;
  const wasOpen = previousOpenRef.current;

  if (open && (!wasOpen || currentIncidentId !== previousIncidentId)) {
    form.reset(buildDefaultValues(incident, mode));
    setSubmitError(null);
  } else if (!open && wasOpen) {
    setSubmitError(null);
  }

  previousIncidentIdRef.current = currentIncidentId;
  previousOpenRef.current = open;
};

export function useIncidentEditorForm({
  mode,
  incident,
  open,
  onSubmit,
  onClose
}: UseIncidentEditorFormProps) {
  const isEditMode = mode === 'edit';

  const form = useForm<IncidentFormData>({
    resolver: zodResolver(incidentSchema) as unknown as Resolver<IncidentFormData>,
    defaultValues: buildDefaultValues(incident, mode),
  }) as UseFormReturn<IncidentFormData>;

  const [submitError, setSubmitError] = useState<string | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const previousIncidentIdRef = useRef<string | null>(null);
  const previousOpenRef = useRef<boolean>(false);

  // Reset form when dialog opens or incident changes
  useEffect(() => {
    syncFormStateOnDialogChanges({
      open,
      incident,
      mode,
      form,
      setSubmitError,
      previousIncidentIdRef,
      previousOpenRef
    });
  }, [open, incident, mode, form]);

  // Focus first field when dialog opens
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => firstFieldRef.current?.focus(), 120);
    return () => clearTimeout(timer);
  }, [open]);

  const handleSubmit = async (values: IncidentFormData) => {
    try {
      setSubmitError(null);
      await onSubmit(values);

      // Reset pristine state after successful save
      form.reset(values);
    } catch (error) {
      setSubmitError(extractErrorMessage(error));
      throw error;
    }
  };

  const handleSaveAndClose = async () => {
    try {
      const valid = await form.trigger();

      if (!valid) {
        return;
      }

      const latestValues = form.getValues();
      await handleSubmit(latestValues);
      onClose();
    } catch (error) {
      console.error('Error during save and close:', error);
    }
  };

  const handleReset = () => {
    form.reset(buildDefaultValues(incident, mode));
    setSubmitError(null);
  };

  return {
    form,
    submitError,
    setSubmitError,
    firstFieldRef,
    handleSubmit,
    handleSaveAndClose,
    handleReset,
    isEditMode,
  };
}
